import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export type ButtonVariant = 'primary' | 'brand' | 'outline' | 'ghost' | 'soft' | 'link' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

/** Variants taken from the approved screens; nothing here is invented. */
const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-white shadow-primary-sm hover:bg-[#1D4ED8]',
  brand: 'bg-brand text-white hover:bg-[#0E2F5C]',
  outline: 'border border-line bg-surface text-ink hover:bg-canvas',
  ghost: 'bg-transparent text-ink-muted hover:bg-line-subtle',
  soft: 'border border-primary-border bg-primary-soft text-primary hover:bg-[#DBEAFE]',
  link: 'bg-transparent p-0 text-primary hover:underline',
  danger: 'bg-danger text-white hover:bg-[#B91C1C]',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'gap-1.5 rounded-lg px-3.5 py-[7px] text-[12.5px]',
  md: 'gap-2 rounded-[9px] px-4 py-2.5 text-[13.5px]',
  lg: 'gap-2.5 rounded-[11px] px-[22px] py-[13px] text-[14px]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Rendered before the label. Use a 15–17px lucide icon to match the design. */
  icon?: ReactNode;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'outline', size = 'md', icon, fullWidth, className, children, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cn(
        'inline-flex items-center justify-center font-semibold transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-55',
        variant === 'link' ? 'text-[13px] font-bold' : SIZE_CLASSES[size],
        VARIANT_CLASSES[variant],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required: icon-only controls must still be announced to screen readers. */
  label: string;
  children: ReactNode;
}

/** The 34x34 square control used in the topbar. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, className, children, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={label}
      title={label}
      className={cn(
        'relative flex h-[34px] w-[34px] items-center justify-center rounded-[9px]',
        'border border-line bg-surface text-ink-muted transition-colors hover:bg-canvas',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
